import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingSuccess() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", data: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", data: null });
      return;
    }
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      try {
        const res = await axios.get(`${API}/bookings/checkout/status/${sessionId}`);
        const data = res.data;
        if (data.payment_status === "paid") {
          setState({ status: "paid", data });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", data });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 10) {
          setState({ status: "pending", data });
          return;
        }
        setTimeout(poll, 2000);
        setState({ status: "pending", data });
      } catch (e) {
        setState({ status: "error", data: null });
      }
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId]);

  return (
    <div data-testid="booking-success-page" className="min-h-screen bg-sand flex items-center justify-center px-6">
      <div className="max-w-xl w-full text-center py-16">
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-terracotta" size={72} />
            <h1 className="font-display text-4xl md:text-5xl mt-6 text-jungle" data-testid="success-title">{t.success.title}</h1>
            <p className="mt-4 text-forest text-lg">{t.success.paid}</p>
          </>
        )}
        {(state.status === "checking" || state.status === "pending") && (
          <>
            <Loader2 className="mx-auto text-terracotta animate-spin" size={64} />
            <h1 className="font-display text-3xl md:text-4xl mt-6 text-jungle">{t.success.title}</h1>
            <p className="mt-4 text-forest text-lg">{t.success.pending}</p>
          </>
        )}
        {(state.status === "expired" || state.status === "error") && (
          <>
            <XCircle className="mx-auto text-forest" size={64} />
            <h1 className="font-display text-3xl md:text-4xl mt-6 text-jungle">{t.success.expired}</h1>
          </>
        )}
        {state.data?.booking && (
          <div className="mt-8 border border-jungle rounded-sm p-6 text-left bg-white">
            <div className="font-overline text-xs text-terracotta">Booking</div>
            <div className="font-display text-xl text-jungle mt-1">{state.data.booking.package_name}</div>
            <div className="text-forest mt-2">{state.data.booking.location}</div>
            <div className="text-forest mt-1">{state.data.booking.preferred_date} · {state.data.booking.passengers} pax</div>
            <div className="text-jungle font-semibold mt-3">Amount: ${state.data.booking.amount} {state.data.booking.currency?.toUpperCase()}</div>
          </div>
        )}
        <Link to="/" data-testid="success-back-btn" className="btn-ghost mt-8 inline-flex">{t.success.back}</Link>
      </div>
    </div>
  );
}
